<template>
  <div id="main_content">
    <section class="advertising-section">
      <div class="container">
        <div class="advertising-shell">
          <div class="section-title text-center advertising-heading">
            <p class="advertising-kicker">{{ $t("advertisingPage.kicker") }}</p>
            <h3 class="sub-title wow pixFadeUp">{{ $t("advertisingPage.title") }}</h3>
            <p class="advertising-intro">{{ $t("advertisingPage.description") }}</p>
          </div>

          <div class="advertising-search-panel">
            <div class="advertising-search-box">
              <span class="advertising-search-icon">?</span>
              <input
                v-model="searchTerm"
                type="search"
                class="advertising-search-input"
                :placeholder="$t('advertisingPage.searchPlaceholder')"
              />
            </div>
          </div>

          <div class="advertising-results-summary" v-if="!isLoading">
            {{ filteredAdvertising.length }}
            {{
              filteredAdvertising.length === 1
                ? $t("advertisingPage.result")
                : $t("advertisingPage.results")
            }}
          </div>

          <div class="advertising-loading" v-if="isLoading">
            {{ $t("advertisingPage.loading") }}
          </div>

          <div class="advertising-grid" v-else-if="filteredAdvertising.length">
            <article
              class="advertising-card"
              v-for="company in filteredAdvertising"
              :key="company.id"
            >
              <div class="advertising-card-media" v-if="company.image_url">
                <img :src="company.image_url" :alt="company.company" />
              </div>

              <div class="advertising-card-body">
                <p class="advertising-card-eyebrow">
                  {{ company.company }}
                </p>
                <h4>{{ company.title }}</h4>
                <p class="advertising-card-description">
                  {{ company.description }}
                </p>

                <div class="advertising-contact-list">
                  <a
                    v-if="company.phone"
                    :href="`tel:${normalizePhone(company.phone)}`"
                    class="advertising-contact-item"
                  >
                    <span>{{ $t("field_phone") }}</span>
                    <strong>{{ company.phone }}</strong>
                  </a>
                  <a
                    v-if="company.email"
                    :href="`mailto:${company.email}`"
                    class="advertising-contact-item"
                  >
                    <span>{{ $t("field_email") }}</span>
                    <strong>{{ company.email }}</strong>
                  </a>
                  <div v-if="company.address" class="advertising-contact-item">
                    <span>{{ $t("field_address") }}</span>
                    <strong>{{ company.address }}</strong>
                  </div>
                </div>
              </div>
            </article>
          </div>

          <div class="advertising-empty" v-else>
            <h4>{{ $t("advertisingPage.emptyTitle") }}</h4>
            <p>{{ $t("advertisingPage.emptyText") }}</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "advertising",
  data() {
    return {
      advertising: [],
      isLoading: false,
      searchTerm: ""
    };
  },
  computed: {
    filteredAdvertising() {
      const search = this.normalizeText(this.searchTerm);

      if (!search) return this.advertising;

      return this.advertising.filter((company) => {
        const searchableText = this.normalizeText(
          [
            company.company,
            company.title,
            company.description,
            company.phone,
            company.email,
            company.address
          ].join(" ")
        );

        return searchableText.includes(search);
      });
    }
  },
  async mounted() {
    await this.getAdvertising();
  },
  methods: {
    async getAdvertising() {
      this.isLoading = true;
      const url = "https://api.mikmic.com/api/";

      try {
        const response = await this.axios.post(url + "advertising/getAllData");
        this.advertising = response.data;
      } finally {
        this.isLoading = false;
      }
    },
    normalizePhone(phone) {
      return String(phone || "").replace(/[^\d+]/g, "");
    },
    normalizeText(value) {
      return String(value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();
    }
  }
};
</script>

<style scoped>
.advertising-section {
  background: #ffffff;
  padding: 135px 0 110px;
}

.advertising-shell {
  max-width: 1120px;
  margin: 0 auto;
}

.advertising-heading {
  margin-bottom: 34px;
}

.advertising-kicker {
  margin-bottom: 10px;
  color: #6d2df7;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.advertising-intro {
  max-width: 720px;
  margin: 16px auto 0;
  color: #6f6b82;
  font-size: 16px;
  line-height: 1.7;
}

.advertising-search-panel {
  padding: 24px;
  border: 1px solid #ece7fb;
  border-radius: 8px;
  background: #f8f6ff;
  box-shadow: 0 18px 45px rgba(21, 13, 63, 0.08);
}

.advertising-search-box {
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 58px;
  padding: 0 18px;
  border: 2px solid transparent;
  border-radius: 8px;
  background: #ffffff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.advertising-search-box:focus-within {
  border-color: #6d2df7;
  box-shadow: 0 0 0 4px rgba(109, 45, 247, 0.12);
}

.advertising-search-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #00e8b8;
  color: #150d3f;
  font-weight: 900;
}

.advertising-search-input {
  width: 100%;
  border: 0;
  outline: 0;
  color: #150d3f;
  font-size: 17px;
  font-weight: 600;
  background: transparent;
}

.advertising-search-input::placeholder {
  color: #8a849d;
  font-weight: 500;
}

.advertising-results-summary {
  margin: 26px 0 16px;
  color: #6f6b82;
  font-size: 14px;
  font-weight: 700;
}

.advertising-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.advertising-card {
  display: flex;
  flex-direction: column;
  min-height: 220px;
  border: 1px solid #ece7fb;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 12px 34px rgba(21, 13, 63, 0.07);
  overflow: hidden;
}

.advertising-card-media {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 210px;
  padding: 18px;
  background: #f8f6ff;
}

.advertising-card-media img {
  display: block;
  width: 100%;
  max-height: 190px;
  height: auto;
  object-fit: contain;
}

.advertising-card-body {
  padding: 22px;
}

.advertising-card-eyebrow {
  margin: 0 0 8px;
  color: #6d2df7;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.advertising-card h4 {
  margin: 0 0 10px;
  color: #150d3f;
  font-size: 22px;
  line-height: 1.25;
}

.advertising-card-description {
  margin: 0 0 18px;
  color: #6f6b82;
  font-size: 15px;
  line-height: 1.65;
}

.advertising-contact-list {
  display: grid;
  gap: 8px;
}

.advertising-contact-item {
  display: grid;
  gap: 2px;
  padding: 10px 12px;
  border-radius: 8px;
  color: #150d3f;
  text-decoration: none;
  background: #f8f6ff;
}

.advertising-contact-item span {
  color: #8a849d;
  font-size: 12px;
  font-weight: 700;
}

.advertising-contact-item strong {
  color: #150d3f;
  font-size: 14px;
  font-weight: 800;
  word-break: break-word;
}

.advertising-contact-item:hover {
  background: #efe9ff;
}

.advertising-loading,
.advertising-empty {
  margin-top: 24px;
  padding: 34px 24px;
  border: 1px dashed #d9cef8;
  border-radius: 8px;
  text-align: center;
  color: #6f6b82;
  background: #fbfaff;
}

.advertising-empty h4 {
  margin-bottom: 8px;
  color: #150d3f;
  font-size: 20px;
}

.advertising-empty p {
  margin: 0;
}

@media (max-width: 991px) {
  .advertising-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .advertising-section {
    padding: 105px 0 80px;
  }

  .advertising-search-panel {
    padding: 16px;
  }

  .advertising-card {
    min-height: 0;
  }

  .advertising-card-media {
    padding: 16px;
  }

  .advertising-card-media img {
    max-height: 178px;
  }
}
</style>
